import React, { useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faSearch, faCalendarAlt, faSortAlphaDown, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";

const appleEntrance = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(235, 235, 245, 0.4);
  backdrop-filter: blur(10px) saturate(120%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #E5E5E7;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;
  animation: ${appleEntrance} 0.5s cubic-bezier(0.16, 1, 0.3, 1);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
    opacity: 0.015;
    pointer-events: none;
    z-index: 1;
  }
`;

const Header = styled.header`
  padding: 24px 24px 20px;
  border-bottom: 1px solid #F2F2F7;
  z-index: 10;

  .title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 20px;
  }

  h2 {
    color: #1D1D1F;
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    letter-spacing: -0.02em;
  }
`;

const BadgeCount = styled.span`
  background: #F5F5F7;
  color: #86868B;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    font-size: 10px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  
  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #86868B;
    font-size: 13px;
  }
`;

const CompactInput = styled.input`
  height: 40px;
  width: 100%;
  background: #F5F5F7;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0 12px 0 40px;
  color: #1D1D1F;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    background: #FFFFFF;
    border-color: #FF8F40;
    box-shadow: 0 0 0 4px rgba(255, 143, 64, 0.15);
  }
`;

const IconButton = styled.button`
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.active ? '#FF8F40' : '#F5F5F7'};
  border: none;
  border-radius: 8px;
  color: ${props => props.active ? '#FFFFFF' : '#86868B'};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 15;

  &:hover {
    background: ${props => props.active ? '#FF8F40' : '#E8E8ED'};
    color: ${props => props.active ? '#FFFFFF' : '#1D1D1F'};
  }
`;

const ListArea = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 12px 24px 24px;
  z-index: 5;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: #D1D1D6; border-radius: 8px; }
`;

const FileCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  margin-top: 10px;
  background: #FFFFFF;
  border: 1px solid #F2F2F7;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #E5E5E7;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  }
`;

const FileMeta = styled.div`
  display: flex;
  flex-direction: column;
  .name { color: #1D1D1F; font-size: 14px; font-weight: 500; margin-bottom: 2px; }
  .date { color: #86868B; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
`;

const DeleteAction = styled.button`
  background: transparent;
  border: none;
  color: #C7C7CC;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    color: #FF3B30;
    background: rgba(255, 59, 48, 0.08);
  }
`;

const Footer = styled.footer`
  padding: 16px 24px;
  background: #F5F5F7;
  display: flex;
  justify-content: flex-end;
  z-index: 10;
`;

const MainButton = styled.button`
  background: #1D1D1F;
  color: #FFFFFF;
  border: none;
  height: 40px;
  padding: 0 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover { opacity: 0.9; }
`;

const parseDate = (dateStr) => {
  if (!dateStr) return 0;
  const timestamp = Date.parse(dateStr);
  if (!isNaN(timestamp)) return timestamp;
  const parts = dateStr.split(/[/ -]/);
  if (parts.length === 3) {
    const [day, month, year] = parts.map(Number);
    return new Date(year, month - 1, day).getTime();
  }
  return 0;
};

const FileListModal = ({ isOpen, onClose, files = [], onDeleteFile }) => {
  const intl = useIntl();
  const [filter, setFilter] = useState("");
  const [sortBy, setSortBy] = useState("date");

  const processedFiles = useMemo(() => {
    let result = [...files];

    if (filter) {
      result = result.filter((f) =>
        f.file.toLowerCase().includes(filter.toLowerCase())
      );
    }

    result.sort((a, b) => {
      if (sortBy === "name") {
        return a.file.localeCompare(b.file);
      } else {
        return parseDate(b.date) - parseDate(a.date);
      }
    });

    return result;
  }, [files, filter, sortBy]);

  if (!isOpen) return null;

  const toggleSort = (type) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSortBy(type);
  };

  const handleContainerClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={handleContainerClick}>
        <Header>
          <div className="title-wrapper">
            <h2>
              {intl.formatMessage({
                id: "fileListModal.title",
                defaultMessage: "Arquivos Carregados",
              })}
            </h2>
            <BadgeCount>
              <FontAwesomeIcon icon={faFileAlt} />
              {processedFiles.length} {processedFiles.length === 1 ? 'documento' : 'documentos'}
            </BadgeCount>
          </div>
          <Toolbar>
            <SearchBox>
              <FontAwesomeIcon icon={faSearch} />
              <CompactInput
                placeholder="Procurar nos arquivos..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </SearchBox>

            <IconButton
              type="button"
              active={sortBy === "name"}
              onClick={toggleSort("name")}
              title="Ordenar por Nome"
            >
              <FontAwesomeIcon icon={faSortAlphaDown} />
            </IconButton>

            <IconButton
              type="button"
              active={sortBy === "date"}
              onClick={toggleSort("date")}
              title="Ordenar por Data"
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
            </IconButton>
          </Toolbar>
        </Header>

        <ListArea>
          {processedFiles.length > 0 ? (
            processedFiles.map((fileObj, index) => (
              <FileCard key={`${fileObj.file}-${index}`}>
                <FileMeta>
                  <span className="name">{fileObj.file}</span>
                  <span className="date">{fileObj.date}</span>
                </FileMeta>
                <DeleteAction
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFile(fileObj.file);
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </DeleteAction>
              </FileCard>
            ))
          ) : (
            <div style={{ textAlign: "center", padding: "60px", color: "#86868B", fontSize: "14px" }}>
              Nenhum arquivo encontrado
            </div>
          )}
        </ListArea>

        <Footer>
          <MainButton type="button" onClick={onClose}>
            Pronto
          </MainButton>
        </Footer>
      </ModalContainer>
    </Overlay>
  );
};

export default FileListModal;